import {
    Avatar, Box, Button, IconButton, useMediaQuery, useTheme, Drawer,
    List, ListItem, ListItemButton, ListItemText, Divider, Collapse,
    InputBase, Badge
} from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import { AddShoppingCart, FavoriteBorder, ExpandLess, ExpandMore } from "@mui/icons-material";
import CategorySheet from "./CategorySheet";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { searchProduct } from "../../../State/customer/productSlice";
import { fetchAllCategories } from "../../../State/customer/categorySlice";

export interface Category {
    id?: number;
    name: string;
    categoryId: string;
    parentCategoryId?: string | null;
    level: number; // 1 = Main, 2 = Subcategory, 3 = Item category
    parentCategoryName?: string;
    children?: Category[];
}

// Inline Logo Component matching header background color (#DE8F07)
const EagleSkylineLogo = () => (
    <svg
        width="180"
        height="50"
        viewBox="0 0 360 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 sm:h-10 w-auto cursor-pointer"
    >
        <rect width="360" height="100" fill="#DE8F07" />
        <path
            d="M20 82 H340 V80 H300 V50 H280 V80 H260 V60 H245 V80 H225 V35 H200 V80 H185 V45 H165 V80 H145 V55 H130 V80 H110 V40 H90 V80 H75 V65 H60 V80 H45 V58 H35 V80 H20 Z"
            fill="#0F172A"
            opacity="0.85"
        />
        <path
            d="M180 18 C160 10 120 18 80 32 C115 35 140 40 162 48 C150 56 138 68 130 80 C152 66 170 58 180 56 C190 58 208 66 230 80 C222 68 210 56 198 48 C220 40 245 35 280 32 C240 18 200 10 180 18 Z"
            fill="#0F172A"
        />
        <path
            d="M180 18 L185 24 L178 26 Z"
            fill="#FFFFFF"
        />
        <text
            x="180"
            y="95"
            textAnchor="middle"
            fill="#0F172A"
            fontSize="14"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
            letterSpacing="1.5"
        >
            EAGLE SKYLINE SOLUTIONS
        </text>
    </svg>
);

interface SearchFormProps {
    searchQuery: string;
    setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
    handleSearchSubmit: (e: React.FormEvent) => void;
    searchInputRef: React.RefObject<HTMLInputElement | null>;
}

const SearchForm: React.FC<SearchFormProps> = ({
    searchQuery,
    setSearchQuery,
    handleSearchSubmit,
    searchInputRef,
}) => (
    <form
        onSubmit={handleSearchSubmit}
        className="flex items-center w-full max-w-[650px] bg-white rounded-full px-3 py-1 shadow-sm border border-gray-200"
    >
        <InputBase
            inputRef={searchInputRef}
            placeholder="Search products, brands, and categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full text-gray-800 font-medium placeholder-gray-400 pl-1"
            sx={{ fontSize: { xs: '13px', sm: '14px' } }}
        />
        {searchQuery && (
            <IconButton onClick={() => setSearchQuery("")} size="small" className="p-1">
                <CloseIcon className="text-gray-400 hover:text-gray-600" sx={{ fontSize: 18 }} />
            </IconButton>
        )}
        <IconButton type="submit" size="small" className="p-1 text-gray-700">
            <SearchIcon sx={{ fontSize: 20 }} />
        </IconButton>
    </form>
);

const Navbar = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [showCategorySheet, setShowCategorySheet] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
    const [expandedSubCategory, setExpandedSubCategory] = useState<string | null>(null);

    const [searchQuery, setSearchQuery] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    const navigate = useNavigate();
    const { auth, cart, wishlist, category: categoryState } = useAppSelector(state => state);
    const categories: Category[] = categoryState?.categories || [];

    const mainCategories = categories.filter(cat => cat.level === 1);

    useEffect(() => {
        dispatch(fetchAllCategories());
    }, [dispatch]);

    useEffect(() => {
        if (mainCategories.length > 0 && !selectedCategory) {
            setSelectedCategory(mainCategories[0].categoryId);
        }
    }, [mainCategories, selectedCategory]);

    const handleDrawerToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleCategoryToggle = (categoryId: string) => {
        setExpandedCategory(prev => prev === categoryId ? null : categoryId);
    };

    const handleSubCategoryToggle = (subCategoryId: string) => {
        setExpandedSubCategory(prev => prev === subCategoryId ? null : subCategoryId);
    };

    const handleDirectNavigation = (path: string) => {
        setMobileMenuOpen(false);
        navigate(path);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedQuery = searchQuery.trim();
        if (trimmedQuery) {
            dispatch(searchProduct(trimmedQuery));
            navigate(`/products?search=${encodeURIComponent(trimmedQuery)}`);
            setSearchQuery("");
            setMobileMenuOpen(false);
        }
    };

    const getSubCategories = (mainCat: Category): Category[] => {
        if (mainCat.children && mainCat.children.length > 0) {
            return mainCat.children;
        }
        return categories.filter(
            (cat) => cat.level === 2 && (cat.parentCategoryId === mainCat.categoryId)
        );
    };

    const getItemCategories = (subCat: Category): Category[] => {
        if (subCat.children && subCat.children.length > 0) {
            return subCat.children;
        }
        return categories.filter(
            (cat) => cat.level === 3 && (cat.parentCategoryId === subCat.categoryId)
        );
    };

    return (
        <>
            {/* Sticky Top Bar Container */}
            <Box 
                className="sticky top-0 left-0 right-0 shadow-md" 
                sx={{ zIndex: 1100, backgroundColor: "#DE8F07" }}
            >
                {/* TOP ROW: Hotline Bar */}
                <div className="bg-white py-1 px-3 sm:px-6 lg:px-20 border-b border-gray-100 flex items-center justify-center text-xs sm:text-sm font-semibold text-gray-800">
                    <div className="flex items-center gap-1.5">
                        <PhoneIcon sx={{ fontSize: 16, color: '#DE8F07' }} />
                        <span>Hotline:</span>
                        <a
                            href="tel:0700870225"
                            className="text-[#DE8F07] hover:underline font-bold"
                        >
                            0700870225
                        </a>
                    </div>
                </div>

                {/* FIRST ROW: Logo, Desktop Search, & Action Icons */}
                <div className="flex items-center justify-between px-3 sm:px-6 lg:px-20 h-[56px] sm:h-[60px] lg:h-[65px] gap-2">

                    {/* Left: Mobile Menu Button & Brand Logo */}
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        {!isLarge && (
                            <IconButton onClick={handleDrawerToggle} className="text-white p-1" size="small">
                                <MenuIcon sx={{ fontSize: 26 }} />
                            </IconButton>
                        )}
                        <div onClick={() => handleDirectNavigation("/")} className="flex items-center cursor-pointer">
                            <EagleSkylineLogo />
                        </div>
                    </div>

                    {/* Desktop Middle: Search Bar */}
                    {isLarge && (
                        <div className="flex-1 flex justify-center px-4">
                            <SearchForm
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                handleSearchSubmit={handleSearchSubmit}
                                searchInputRef={searchInputRef}
                            />
                        </div>
                    )}

                    {/* Right: User Actions */}
                    <div className="flex gap-0.5 sm:gap-2 lg:gap-4 items-center flex-shrink-0">
                        {auth.user ? (
                            <IconButton onClick={() => handleDirectNavigation("/account/orders")} className="p-1">
                                <Avatar sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }} />
                                <span className="font-semibold hidden lg:block text-white ml-2 text-sm truncate max-w-[120px]">
                                    {auth.user?.fullName}
                                </span>
                            </IconButton>
                        ) : (
                            <Button
                                onClick={() => handleDirectNavigation("/login")}
                                variant="contained"
                                size="small"
                                disableElevation
                                sx={{
                                    bgcolor: 'white',
                                    color: '#DE8F07',
                                    fontSize: { xs: '0.7rem', sm: '0.85rem' },
                                    px: { xs: 1.2, sm: 2 },
                                    py: 0.4,
                                    fontWeight: 'bold',
                                    borderRadius: '20px',
                                    '&:hover': { bgcolor: '#f8f8f8' }
                                }}
                            >
                                Login
                            </Button>
                        )}

                        <IconButton onClick={() => handleDirectNavigation("/wishlist")} className="text-white p-1.5">
                            <Badge badgeContent={wishlist.wishList?.products?.length || 0} color="error">
                                <FavoriteBorder sx={{ fontSize: { xs: 22, sm: 25 } }} />
                            </Badge>
                        </IconButton>

                        <IconButton onClick={() => handleDirectNavigation("/cart")} className="text-white p-1.5">
                            <Badge badgeContent={cart.cart?.cartItems?.length || 0} color="error">
                                <AddShoppingCart sx={{ fontSize: { xs: 22, sm: 25 } }} />
                            </Badge>
                        </IconButton>
                    </div>
                </div>

                {/* SECOND ROW / MOBILE LOWER SEARCH ROW */}
                {!isLarge ? (
                    <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
                        <SearchForm
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            handleSearchSubmit={handleSearchSubmit}
                            searchInputRef={searchInputRef}
                        />
                    </div>
                ) : (
                    <div className="relative px-20 border-b border-gray-200 bg-gray-100 text-gray-900">
                        <ul className="flex items-center gap-1 font-semibold text-sm">
                            {mainCategories.map((item) => (
                                <li
                                    key={item.categoryId}
                                    onMouseLeave={() => setShowCategorySheet(false)}
                                    onMouseEnter={() => {
                                        setShowCategorySheet(true);
                                        setSelectedCategory(item.categoryId);
                                    }}
                                    className="hover:bg-gray-200 text-gray-900 py-2.5 px-4 rounded-t flex items-center cursor-pointer transition-colors duration-150"
                                >
                                    {item.name}
                                </li>
                            ))}
                        </ul>

                        {showCategorySheet && (
                            <div
                                onMouseLeave={() => setShowCategorySheet(false)}
                                onMouseEnter={() => setShowCategorySheet(true)}
                                className="categorySheet absolute top-full left-20 right-20 border border-gray-200 bg-white shadow-xl z-50"
                            >
                                <CategorySheet
                                    selectedCategory={selectedCategory}
                                    categories={categories}
                                    onClose={() => setShowCategorySheet(false)}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Box>

            {/* Mobile Side Drawer - Positioned outside sticky Box with high z-index */}
            <Drawer
                anchor="left"
                open={mobileMenuOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{ zIndex: 1300 }}
                PaperProps={{
                    sx: {
                        width: { xs: '85vw', sm: 320 },
                        maxWidth: 340
                    }
                }}
            >
                <Box className="flex flex-col h-full justify-between">
                    
                    {/* Upper Drawer Header & Category List */}
                    <div className="overflow-y-auto px-3 py-2 flex-1">
                        
                        {/* Drawer Title Header */}
                        <div className="flex items-center justify-between pb-2 pt-1 border-b border-gray-100">
                            <h2 className="text-lg font-bold text-amber-600">Eagle Skyline Solutions</h2>
                            <IconButton onClick={handleDrawerToggle} size="small" className="p-1">
                                <CloseIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </div>

                        {/* Search Bar inside Drawer to give instant search access */}
                        <div className="my-2">
                            <SearchForm
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                handleSearchSubmit={handleSearchSubmit}
                                searchInputRef={searchInputRef}
                            />
                        </div>

                        {/* Category List - Starts strictly after the search bar */}
                        <div className="pt-2">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1.5 mb-1">
                                Categories
                            </p>
                            <List component="nav" disablePadding>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleDirectNavigation("/")}
                                        sx={{ py: 1, px: 1.5, borderRadius: 1 }}
                                    >
                                        <ListItemText
                                            primary="Home"
                                            primaryTypographyProps={{ fontSize: '0.925rem', fontWeight: '600' }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <Divider className="my-1" />

                                {/* Level 1 Main Categories */}
                                {mainCategories.map((mainCat) => {
                                    const isExpanded = expandedCategory === mainCat.categoryId;
                                    const subCategories = getSubCategories(mainCat);

                                    return (
                                        <React.Fragment key={mainCat.categoryId}>
                                            <ListItem disablePadding>
                                                <ListItemButton
                                                    onClick={() => handleCategoryToggle(mainCat.categoryId)}
                                                    sx={{ py: 1, px: 1.5, borderRadius: 1 }}
                                                >
                                                    <ListItemText
                                                        primary={mainCat.name}
                                                        primaryTypographyProps={{
                                                            fontSize: '0.9rem',
                                                            fontWeight: isExpanded ? '700' : '500',
                                                            color: isExpanded ? '#DE8F07' : 'text.primary'
                                                        }}
                                                    />
                                                    {subCategories.length > 0 && (
                                                        isExpanded ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />
                                                    )}
                                                </ListItemButton>
                                            </ListItem>

                                            {/* Level 2 Subcategories */}
                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <Box className="bg-orange-50/40 rounded-lg p-2 my-1 border-l-2 border-amber-500">
                                                    <ListItemButton
                                                        onClick={() => handleDirectNavigation(`/products/${mainCat.categoryId}`)}
                                                        sx={{ py: 0.5, px: 1, mb: 1, borderRadius: 1 }}
                                                    >
                                                        <ListItemText
                                                            primary={`All ${mainCat.name}`}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.825rem',
                                                                color: '#DE8F07',
                                                                fontWeight: '700'
                                                            }}
                                                        />
                                                    </ListItemButton>

                                                    <List disablePadding>
                                                        {subCategories.map((subCat) => {
                                                            const isSubExpanded = expandedSubCategory === subCat.categoryId;
                                                            const itemCategories = getItemCategories(subCat);

                                                            return (
                                                                <React.Fragment key={subCat.categoryId}>
                                                                    <ListItemButton
                                                                        onClick={() => {
                                                                            if (itemCategories.length > 0) {
                                                                                handleSubCategoryToggle(subCat.categoryId);
                                                                            } else {
                                                                                handleDirectNavigation(`/products/${subCat.categoryId}`);
                                                                            }
                                                                        }}
                                                                        sx={{ py: 0.5, px: 1, borderRadius: 1 }}
                                                                    >
                                                                        <ListItemText
                                                                            primary={subCat.name}
                                                                            primaryTypographyProps={{
                                                                                fontSize: '0.85rem',
                                                                                fontWeight: '600',
                                                                                color: 'text.secondary'
                                                                            }}
                                                                        />
                                                                        {itemCategories.length > 0 && (
                                                                            isSubExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />
                                                                        )}
                                                                    </ListItemButton>

                                                                    {/* Level 3 Item Categories */}
                                                                    {itemCategories.length > 0 && (
                                                                        <Collapse in={isSubExpanded} timeout="auto" unmountOnExit>
                                                                            <List disablePadding className="pl-3">
                                                                                {itemCategories.map((itemCat) => (
                                                                                    <ListItemButton
                                                                                        key={itemCat.categoryId}
                                                                                        onClick={() => handleDirectNavigation(`/products/${itemCat.categoryId}`)}
                                                                                        sx={{ py: 0.25, px: 1, borderRadius: 1 }}
                                                                                    >
                                                                                        <ListItemText
                                                                                            primary={itemCat.name}
                                                                                            primaryTypographyProps={{
                                                                                                fontSize: '0.8rem',
                                                                                                color: 'text.secondary'
                                                                                            }}
                                                                                        />
                                                                                    </ListItemButton>
                                                                                ))}
                                                                            </List>
                                                                        </Collapse>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </List>
                                                </Box>
                                            </Collapse>
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </div>
                    </div>

                    {/* Bottom User Section */}
                    <div className="p-3 bg-gray-50 border-t border-gray-200">
                        {auth.user ? (
                            <ListItemButton
                                onClick={() => handleDirectNavigation("/account/orders")}
                                sx={{ borderRadius: 1, px: 1, py: 1 }}
                            >
                                <Avatar sx={{ width: 32, height: 32, mr: 1.5 }} />
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs text-gray-500 font-medium">Logged in as</p>
                                    <p className="text-sm font-semibold text-gray-800 truncate">{auth.user.fullName}</p>
                                </div>
                            </ListItemButton>
                        ) : (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => handleDirectNavigation("/login")}
                                disableElevation
                                sx={{
                                    backgroundColor: '#DE8F07',
                                    py: 1,
                                    fontWeight: 'bold',
                                    '&:hover': { backgroundColor: '#c27c06' }
                                }}
                            >
                                Login / Register
                            </Button>
                        )}
                    </div>
                </Box>
            </Drawer>
        </>
    );
};

export default Navbar;